/**
 * VerificationStatusCard Component
 * Dashboard card showing verification status and progress
 */
import React, { useState } from 'react';
import TierBadge from './TierBadge';
import ApplyVerificationModal from './ApplyVerificationModal';
import { useVerification } from '../../hooks/useVerification';

const VerificationStatusCard = () => {
  const { data, isLoading, error, apply, recheck, refresh } = useVerification();
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };
  
  const handleApply = async (tier) => {
    try {
      await apply(tier);
      showToast('Application submitted successfully!');
      refresh();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  
  const handleRecheck = async () => {
    try {
      await recheck();
      showToast('Verification rechecked!');
    } catch (err) {
      if (err.message === 'RATE_LIMIT') {
        showToast('Rate limit reached. You can recheck 3 times per day.', 'warning');
      } else {
        showToast(err.message, 'error');
      }
    }
  };
  
  if (isLoading && !data) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }
  
  if (!data) return null;
  
  // Calculate progress to next tier
  const getProgressHints = () => {
    const hints = [];
    const { checks, metrics } = data;
    
    const currentTierNum = parseInt(data.tier[1]) || 0;
    
    // T1 hints
    if (currentTierNum < 1) {
      if (!checks.id_verified) hints.push({ label: 'Complete identity verification', progress: 0 });
      if (!checks.stripe_payout_ready) hints.push({ label: 'Set up Stripe payouts', progress: 0 });
      if (!checks.profile_complete) hints.push({ label: 'Complete your profile', progress: 0 });
      if (checks.portfolio_items < 3) hints.push({ 
        label: `Add ${3 - checks.portfolio_items} more portfolio items`,
        progress: (checks.portfolio_items / 3) * 100
      });
    }
    
    // T2 hints
    if (currentTierNum < 2) {
      if (metrics.orders_completed < 10) hints.push({
        label: `Complete ${10 - metrics.orders_completed} more orders`,
        progress: (metrics.orders_completed / 10) * 100
      });
      if (metrics.nails_avg < 4.7) hints.push({
        label: `Improve rating to 4.7 (currently ${metrics.nails_avg.toFixed(1)})`,
        progress: (metrics.nails_avg / 4.7) * 100
      });
      if (metrics.on_time_rate < 0.90) hints.push({
        label: `Improve on-time rate to 90% (currently ${Math.round(metrics.on_time_rate * 100)}%)`,
        progress: (metrics.on_time_rate / 0.90) * 100
      });
    }
    
    // T3 hints
    if (currentTierNum < 3) {
      if (metrics.lifetime_orders < 40) hints.push({
        label: `Complete ${40 - metrics.lifetime_orders} more lifetime orders`,
        progress: (metrics.lifetime_orders / 40) * 100
      });
      if (metrics.nails_avg < 5.1) hints.push({
        label: `Maintain rating of 5.1+ (currently ${metrics.nails_avg.toFixed(1)})`,
        progress: (metrics.nails_avg / 5.1) * 100
      });
    }
    
    return hints.slice(0, 5); // Show top 5 hints
  };
  
  const progressHints = getProgressHints();
  
  return (
    <>
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TierBadge tier={data.tier} size="lg" showUnverified />
              <div>
                <h2 className="text-xl font-bold">Verification Status</h2>
                <p className="text-sm text-gray-600">
                  Last updated: {new Date(data.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Manage
            </button>
          </div>
        </div>
        
        {/* Status */}
        {data.status !== 'none' && (
          <div className="p-6 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <span className="font-medium">Application Status:</span>
              <span className={`
                px-3 py-1 rounded-full text-sm font-medium
                ${data.status === 'approved' ? 'bg-green-100 text-green-700' : ''}
                ${data.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : ''}
                ${data.status === 'denied' ? 'bg-red-100 text-red-700' : ''}
              `}>
                {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
              </span>
              {data.requested_tier && (
                <>
                  <span className="text-gray-600">for</span>
                  <TierBadge tier={data.requested_tier} size="sm" />
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Progress Hints */}
        {progressHints.length > 0 && (
          <div className="p-6">
            <h3 className="font-semibold mb-4">Progress to Next Tier</h3>
            <div className="space-y-3">
              {progressHints.map((hint, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{hint.label}</span>
                    {hint.progress > 0 && (
                      <span className="text-gray-500">{Math.round(hint.progress)}%</span>
                    )}
                  </div>
                  {hint.progress > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(hint.progress, 100)}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Request Review
              </button>
              <button
                onClick={handleRecheck}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                {isLoading ? 'Rechecking...' : 'Recheck Now'}
              </button>
            </div>
          </div>
        )}
        
        {/* No progress hints - already at highest tier */}
        {progressHints.length === 0 && data.tier !== 'T0' && (
          <div className="p-6 text-center">
            <div className="text-4xl mb-2">🎉</div>
            <p className="text-lg font-semibold text-gray-900">
              {data.tier === 'T3' ? "You're an Elite Partner!" : "Congratulations!"}
            </p>
            <p className="text-gray-600">
              {data.tier === 'T3' 
                ? 'You have achieved the highest verification tier.'
                : 'Keep up the great work to unlock higher tiers.'}
            </p>
          </div>
        )}
      </div>
      
      {/* Modal */}
      <ApplyVerificationModal
        open={showModal}
        onClose={() => setShowModal(false)}
        current={data}
        onApply={handleApply}
        onRecheck={handleRecheck}
      />
      
      {/* Toast */}
      {toast && (
        <div className={`
          fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50
          ${toast.type === 'success' ? 'bg-green-600 text-white' : ''}
          ${toast.type === 'error' ? 'bg-red-600 text-white' : ''}
          ${toast.type === 'warning' ? 'bg-yellow-600 text-white' : ''}
        `}>
          {toast.message}
        </div>
      )}
    </>
  );
};

export default VerificationStatusCard;
